/**
 * Das optionale Passwort einer Prüfung — nötig zum Herunterladen und zum Löschen auf dem Gerät
 * (Vorgabe des Nutzers, 2026-10-09; Vorbild: das Prüfungspasswort der alten Classic-App).
 *
 * **Ablageform wie bei Relay** (`exam.devicePassword`, Relay `00426bb`):
 * `pbkdf2-sha512$<runden>$<salz-base64>$<hash-base64>`, PBKDF2-HMAC-SHA512, 64-Byte-Hash. Relay
 * liefert sie fertig im Dokument; für eine Classic-Prüfung bildet Stage sie beim Herunterladen
 * aus dem Klartext der Prüfungsliste. So prüft das Löschen offline auf beiden Gleisen gleich —
 * und auf dem Gerät liegt nie ein Klartext.
 *
 * ⚠️ **Die Rundenzahl kommt aus dem String**, nicht aus einer Konstante (Bitte von Relay): wer
 * sie dort erhöht, darf Stage nicht brechen.
 */
export const RUNDEN = 210_000;
const PRAEFIX = 'pbkdf2-sha512';
const LAENGE_BITS = 512;

const zuBase64 = (b: Uint8Array) => btoa(String.fromCharCode(...b));
const ausBase64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

async function ableiten(passwort: string, salz: Uint8Array<ArrayBuffer>, runden: number): Promise<Uint8Array> {
  const schluessel = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(passwort),
    'PBKDF2',
    false,
    ['deriveBits'],
  );
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-512', salt: salz, iterations: runden },
    schluessel,
    LAENGE_BITS,
  );
  return new Uint8Array(bits);
}

/** Zerlegt die Ablageform; `null`, wenn sie nicht lesbar ist. */
export function zerlege(ablage: string): { runden: number; salz: Uint8Array<ArrayBuffer>; hash: Uint8Array } | null {
  const teile = ablage.split('$');
  if (teile.length !== 4 || teile[0] !== PRAEFIX) return null;
  const runden = Number(teile[1]);
  if (!Number.isSafeInteger(runden) || runden < 1) return null;
  try {
    return { runden, salz: ausBase64(teile[2]), hash: ausBase64(teile[3]) };
  } catch {
    return null;
  }
}

/** Stimmt die Eingabe? Eine unlesbare Ablage lässt **niemanden** durch. */
export async function pruefePasswort(ablage: string, eingabe: string): Promise<boolean> {
  const teile = zerlege(ablage);
  if (!teile) return false;
  const neu = await ableiten(eingabe, teile.salz, teile.runden);
  if (neu.length !== teile.hash.length) return false;
  // Gleich lang verglichen, ohne frühen Ausstieg.
  let unterschied = 0;
  for (let i = 0; i < neu.length; i += 1) unterschied |= neu[i] ^ teile.hash[i];
  return unterschied === 0;
}

/** Bildet die Ablageform aus einem Klartext (Classic). */
export async function bildeAblage(passwort: string, runden = RUNDEN): Promise<string> {
  const salz = crypto.getRandomValues(new Uint8Array(16));
  const hash = await ableiten(passwort, salz, runden);
  return [PRAEFIX, String(runden), zuBase64(salz), zuBase64(hash)].join('$');
}
